import React, { useState, useEffect, useRef, useCallback } from "react";
import { FedaCheckoutButton } from "fedapay-reactjs";

const FedaPayButton = ({
  amount,
  description,
  onSuccess,
  buttonText,
  disabled,
  isMenuItem,
  classe,
  tour_id,
  paymentType
}) => {
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [apiError, setApiError] = useState(null);
  const buttonRef = useRef(null);
  const initializedRef = useRef(false);

  const PUBLIC_KEY = process.env.REACT_APP_FEDAPAY_PUBLIC_KEY;

  if (!PUBLIC_KEY) {
    throw new Error("Clé publique FedaPay non configurée. Vérifiez REACT_APP_FEDAPAY_PUBLIC_KEY dans .env");
  }

  const handleWrapperClick = (e) => {
    if (disabled) {
      e.preventDefault();
      e.stopPropagation();
      return false;
    }
    return true;
  };

  // Gérer style du bouton en fonction du statut
  useEffect(() => {
    if (buttonRef.current) {
      const btn = buttonRef.current.querySelector('button');
      if (btn) {
        btn.style.pointerEvents = disabled ? 'none' : 'auto';
        btn.style.opacity = disabled ? '0.6' : '1';
        btn.style.cursor = disabled ? 'not-allowed' : 'pointer';
      }
    }
  }, [disabled]);

  // ✅ Initialisation sécurisée du script FedaPay
  useEffect(() => {
    if (initializedRef.current) return;

    initializedRef.current = true;

    const initializeFedaPay = async () => {
      try {
        if (!window.FedaPay) {
          await new Promise((resolve, reject) => {
            const script = document.createElement("script");
            script.src = "https://cdn.fedapay.com/checkout.js";
            script.async = true;
            script.onload = resolve;
            script.onerror = reject;
            document.body.appendChild(script);
          });
        }

        if (!window.FedaPay) {
          throw new Error("FedaPay n'a pas été chargé correctement");
        }

        setIsScriptLoaded(true);
      } catch (error) {
        console.error("Erreur FedaPay:", error);
        setApiError(error.message);
      }
    };

    initializeFedaPay();
  }, []);

  useEffect(() => {
  if (buttonRef.current && isMenuItem) {
    const button = buttonRef.current.querySelector("button");
    const handleClick = (e) => e.stopPropagation();
    if (button) button.addEventListener("click", handleClick);
    return () => {
      if (button) button.removeEventListener("click", handleClick);
    };
  }
}, [isMenuItem]);

  const saveTransaction = useCallback(async (response) => {
    console.log("[FedaPayButton] Début de saveTransaction avec response:", response);
    try {
      const user_id = localStorage.getItem("IDDDD");
      if (!user_id) throw new Error("ID utilisateur introuvable");

      const transactionData = {
        tour_id,
        type_paiement: paymentType,
        montant: amount,
        reference: response.transaction.id,
        statut: response.transaction.status || "completed",
        donnees_paiement: {
          fedapay_response: response.transaction,
          customer: {
            firstname: response.transaction.customer?.firstname || "",
            lastname: response.transaction.customer?.lastname || "",
            email: response.transaction.customer?.email || ""
          }
        }
      };
      console.log("[FedaPayButton] Données de transaction préparées:", transactionData);

      const res = await fetch(`${process.env.REACT_APP_HOTE}/save-transaction/${user_id}/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(transactionData)
      });

      const contentType = res.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const text = await res.text();
        throw new Error(`Réponse inattendue: ${text.slice(0, 100)}...`);
      }

      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.error || "Erreur serveur");
      }

      const data = await res.json();
      console.log("[FedaPayButton] Données sauvegardées avec succès:", data);


      localStorage.setItem("paymentSuccess", "true");
      localStorage.setItem("paymentData", JSON.stringify({
        transaction_id: response.transaction.id,
        amount: data.montant
      }));

      return data;
    } catch (err) {
      console.error("Erreur transaction:", err);
      throw new Error(err.message.includes("Unexpected token") ? "Erreur de communication avec le serveur" : err.message);
    }
  }, [amount, tour_id, paymentType]);

  const handlePaymentComplete = async (response) => {
    console.log("[FedaPayButton] handlePaymentComplete déclenché avec:", response);
    if (isProcessing) return;
    console.log("[FedaPayButton] Paiement déjà en cours, ignore");
    setIsProcessing(true);

    if (response.reason === window.FedaPay.DIALOG_DISMISSED) {
      console.log("[FedaPayButton] Paiement annulé par l'utilisateur");
      setIsProcessing(false);
      return;
    }

    try {
      console.log("[FedaPayButton] Tentative de sauvegarde de la transaction...");
      const data = await saveTransaction(response);
      onSuccess({
        ...data,
        fedapay_response: response.transaction,
        customer_fullname: `${response.transaction.customer?.firstname || ""} ${response.transaction.customer?.lastname || ""}`
      });
    } catch (err) {
      alert("Erreur lors de l’enregistrement de la transaction.");
    } finally {
      setIsProcessing(false);
    }
  };

  if (apiError) {
    return (
      <div className="alert alert-danger">
        {apiError}
        <button className="btn btn-sm btn-light" onClick={() => window.location.reload()}>Réessayer</button>
      </div>
    );
  }

  if (!isScriptLoaded) return null;

  const paymentOptions = {
    public_key: PUBLIC_KEY,
    transaction: {
      amount,
      description
    },
    currency: { iso: "XOF" },
    button: {
      class: classe,
      text: buttonText || `Payer ${amount.toLocaleString()} FCFA`,
      disabled: disabled || isProcessing
    },
    onComplete: handlePaymentComplete,
    onError: (err) => {
      console.error("Erreur FedaPay:", err);
      alert("Erreur de paiement.");
    }
  };

  return (
    <div ref={buttonRef} onClick={handleWrapperClick}>
      <FedaCheckoutButton
        options={paymentOptions}
        key="fedapay-button-static"
      />
    </div>
  );
};

export default React.memo(FedaPayButton);
